// EM-25 (MAJLIS-34): while THIS device is playing a phrase, this seat's microphone does not reach the engine.
//
// The owner found it on his phone: he tapped Replay, the iPhone's speaker played the phrase, his own open
// microphone heard it, and it came back as a new phrase from his seat. Echo cancellation did not remove it —
// it is reliable for WebRTC remote tracks, not for the output of an <audio> element — so the fix is not an
// audio-processing flag but a gate: from the moment a phrase starts playing until it ends, pauses or fails,
// plus a short tail for the room's reverb, nothing this microphone hears is sent.
//
// THE GATE IS NOT MUTE. Mute is the person's own choice and the room is told about it (the other seats see it).
// The gate is local, invisible to the room, and always applied — a browser cannot reliably tell us whether he
// is wearing headphones. The two are separate state and they compose: a gate that ends while he is muted
// leaves him muted; unmuting while a phrase plays leaves the microphone off until the gate ends.
//
// ONE WRITER. `MediaStreamTrack.enabled` is also flipped by livekit-client's own mute()/unmute() (publish.ts
// calls them for the person's mute), so a gate that simply set the flag would be undone by the next unmute.
// This class is the only place that computes and writes it — `enabled = not muted and no holds` — and the
// publisher re-asserts through `reapply()` after each LiveKit call settles.
//
// D-38 (MAJLIS-62): while this phone replays a phrase, the OTHER seats' live voices are silent on this phone only,
// for "the same span as the gate (clip plus tail)", ending "on every path the gate ends on". So the silence is not a
// second state kept beside this one: it IS this gate's `gated()`, told to whoever `watch`es it on every change -
// every path that opens the gate (ended, paused, failed, the fuse, the watchdog, reset) ends the silence with it.
//
// MAJLIS-64 (D-43(2), D-43(4), UI-C24, proposal section 5, consultant rulings A and B): a SPOKEN TRANSLATION holds this
// same gate - one gate, one writer of `track.enabled` - because the browser's echo canceller does not cover an HTTP
// <audio> element either, so the gate is the echo defence. What differs is what the LIVE voices do meanwhile: a
// replay SILENCES them (D-38), a voice LOWERS them (muted on iOS, ruling B). So every hold has a kind, and
// `watchKinds` says which kind is holding (the tail belongs to the kind whose last hold started it).
import type { Clock, TimerId } from "../clock";

/** What is holding the gate: a replay of an original recording (D-38) or a spoken translation (D-43). */
export type HoldKind = "replay" | "voice";

/** The gate's state by kind: `gated` is `gated()`; `replay` / `voice` say which kind holds it (tail included). */
export interface GateKinds {
  gated: boolean;
  replay: boolean;
  voice: boolean;
}

/** ASSUMPTION (EM-25): the room's own reverb outlives the last sample by this much. Not measured.
 *
 * The tail does two jobs. The first is that reverb. The second was found by testing a phrase switch against
 * this gate rather than a stub: when he starts another phrase, the first playback's hold is released at once
 * while the next clip is still being fetched, and it is the tail - not the player - that keeps the microphone
 * shut across that gap. If a fetch ever outlasts the tail the microphone does open in between, which is
 * correct, because nothing is sounding then. */
export const GATE_TAIL_MS = 300;

/** What the gate switches: the one captured track (mic.ts's MicSource). */
export interface GateTarget {
  setEnabled(on: boolean): void;
}

export class MicGate {
  private muted = false;
  private holds = 0;
  private held: Record<HoldKind, number> = { replay: 0, voice: 0 };
  /** The kind whose release started the running tail. */
  private tailKind: HoldKind | null = null;
  private tail: TimerId | undefined = undefined;
  private target: GateTarget | null = null;
  private watchers = new Set<(gated: boolean) => void>();
  private kindWatchers = new Set<(k: GateKinds) => void>();
  private told = false;
  private toldKinds: GateKinds = { gated: false, replay: false, voice: false };
  private telling = false;
  private tellAgain = false;

  constructor(private readonly clock: Clock) {}

  /** The room screen hands the one captured track over when it opens, and null when it stops it. */
  attach(target: GateTarget | null): void {
    this.target = target;
    this.apply();
  }

  /** The person's own mute (seat state). Never changed by a gate. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.apply();
  }

  /** True while a phrase this device plays is holding the microphone shut (tail included). */
  gated(): boolean {
    return this.holds > 0 || this.tail !== undefined;
  }

  /**
   * Hold the gate for one playback; returns its release. Overlapping plays each take a hold and the gate opens
   * only after the last one is released and the tail has run. Releasing twice is harmless (the player calls it
   * from `ended` and from `stop`).
   */
  acquire(kind: HoldKind = "replay"): () => void {
    this.clearTail();
    this.holds += 1;
    this.held[kind] += 1;
    this.apply();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holds -= 1;
      this.held[kind] -= 1;
      if (this.holds === 0) this.startTail(kind);
      this.apply();
    };
  }

  /** Which kind holds the gate now. A hold of either kind counts while it lasts; in the tail, the kind that started it. */
  kinds(): GateKinds {
    const inTail = this.holds === 0 && this.tail !== undefined;
    return {
      gated: this.gated(),
      replay: this.held.replay > 0 || (inTail && this.tailKind === "replay"),
      voice: this.held.voice > 0 || (inTail && this.tailKind === "voice"),
    };
  }

  /** MAJLIS-64: be told every time `kinds()` changes (hearing lowers or silences, the voice queue yields to a
   * replay); returns the unsubscribe. */
  watchKinds(fn: (k: GateKinds) => void): () => void {
    this.kindWatchers.add(fn);
    return () => this.kindWatchers.delete(fn);
  }

  /** D-38: be told every time `gated()` changes (the live voices follow it); returns the unsubscribe. */
  watch(fn: (gated: boolean) => void): () => void {
    this.watchers.add(fn);
    return () => this.watchers.delete(fn);
  }

  /** Re-assert the flag after something else (livekit-client's mute/unmute) has written it. */
  reapply(): void {
    this.apply();
  }

  /** Leaving the room: drop every hold and any tail, so a new room starts from a known state. */
  reset(): void {
    this.clearTail();
    this.holds = 0;
    this.held = { replay: 0, voice: 0 };
    this.apply();
  }

  /** A watcher may change the gate while being told (a replay's hold makes the voice queue release its own): the
   * nested change is told after the current round, so every watcher ends on the gate's LAST state, never a stale one. */
  private tellKinds(): void {
    if (this.telling) {
      this.tellAgain = true;
      return;
    }
    this.telling = true;
    try {
      do {
        this.tellAgain = false;
        const k = this.kinds();
        const t = this.toldKinds;
        if (k.gated === t.gated && k.replay === t.replay && k.voice === t.voice) continue;
        this.toldKinds = k;
        for (const fn of this.kindWatchers) fn(k);
      } while (this.tellAgain);
    } finally {
      this.telling = false;
    }
  }

  private startTail(kind: HoldKind): void {
    this.tailKind = kind;
    this.tail = this.clock.setTimeout(() => {
      this.tail = undefined;
      this.tailKind = null;
      this.apply();
    }, GATE_TAIL_MS);
  }

  private clearTail(): void {
    if (this.tail !== undefined) this.clock.clearTimeout(this.tail);
    this.tail = undefined;
    this.tailKind = null;
  }

  private apply(): void {
    const gated = this.gated();
    this.target?.setEnabled(!this.muted && !gated);
    this.tellKinds();
    if (gated === this.told) return;
    this.told = gated;
    for (const fn of this.watchers) fn(gated);
  }
}
